import React, { useContext, useEffect, useMemo } from "react";
import classNames from "classnames";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { getPeriodKey, isNLevelClassReport } from "@common/helpers/reports";
import { ProfitAndLossResponse } from "@common/types/reports/types";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import { hasSingleValueColumn } from "../singleColumnReport";
import {
    buildProfitLossConfig,
    buildProfitLossView,
} from "./profitLossViewConfig";
import {
    ProfitLossDrillDownParams,
    PLTableMeta,
} from "./profitLossDrillDown.lib";
import { ProfitLossViewRow } from "./profitLossViewTypes";
import styles from "./ProfitLossTable.module.scss";

interface Props {
    report: ProfitAndLossResponse;
    rows: ProfitLossViewRow[];
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    tableRef: React.RefObject<ViewTableHandle | null>;
    onDrillDown: (params: ProfitLossDrillDownParams) => void;
}

/**
 * P&L table rendered via the shared ViewTable runtime.
 * - Columns are built dynamically from report.periods (ephemeral, never persisted)
 * - Total-period columns are pinned right via view.columns[i].options.pin
 * - Expand state is controlled from the parent so the toolbar can toggle it
 * - No DB view state; no query caching (handled in useProfitAndLossQuery)
 */
export const ProfitLossTable: React.FC<Props> = ({
    report,
    rows,
    expanded,
    onExpandedChange,
    tableRef,
    onDrillDown,
}) => {
    const { numberFormat } = useContext(AccountingReportContext);

    const view = useMemo(
        () => buildProfitLossView(report.periods),
        [report.periods],
    );

    const tableMeta: PLTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );

    const config = useMemo(
        () => buildProfitLossConfig(report.periods, numberFormat),
        [report.periods, numberFormat],
    );

    const isByClassReport = useMemo(
        () => isNLevelClassReport(report.periods),
        [report.periods],
    );

    // Derive a key from the report identity so ViewTable remounts when
    // entity or date range changes (resets column sizing, scroll, etc).
    const reportKey = useMemo(
        () => report.periods.map(getPeriodKey).join("|"),
        [report.periods],
    );

    // Scroll to the rightmost column when the report changes so the most
    // recent period (or the total column) is visible on load.
    useEffect(() => {
        const timerId = window.setTimeout(() => {
            tableRef.current?.scrollTo({ left: Number.MAX_SAFE_INTEGER });
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [report, tableRef]);

    return (
        <ViewTable
            key={reportKey}
            ref={tableRef}
            data={rows}
            view={view}
            config={config}
            className={classNames(styles.profitLossTable, {
                [styles.byClassReport]: isByClassReport,
            })}
            centerColumns={hasSingleValueColumn(report.periods)}
            tableMeta={tableMeta}
            rowBorders={false}
            enableColumnResizing
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            nestingIndentPx={16}
            fixedTopOffsetPx
        />
    );
};
